export const siteData = {
  nav: {
    logo: "bikes.rentals",
    links: [
      { label: "How it works", href: "/#how-it-works" },
      { label: "See the product", href: "/demo" },
      { label: "Pricing", href: "/#pricing" },
      { label: "About", href: "/about" },
    ],
    cta: { label: "Start setup", sublabel: "Talk to founder", href: "/start-setup" },
  },
  hero: {
    headline: "Run your bike rental.",
    headlineAccent: "Not your inbox.",
    subheadline:
      "Clear booking and operational workflows that reduce daily chaos, customer questions, and manual coordination. Built for rental operators who want controlled, reliable daily operations.",
    primaryCta: { label: "See how it works", href: "/demo" },
    secondaryCta: { label: "Request demo", href: "/request-demo" },
  },
  booking: {
    headline: "Complete booking. Built to prevent mistakes.",
    intro:
      "A full booking engine with guided flows, customer-managed changes, and built-in error prevention \u2014 designed to reduce confusion before it reaches your daily operations.",
    points: [
      {
        icon: "ShieldCheck",
        text: "Full booking engine with guided, error-preventing flow",
      },
      {
        icon: "UserCheck",
        text: "Customers modify, extend, or cancel bookings themselves",
      },
      {
        icon: "AlertOctagon",
        text: "Booking errors prevented before they affect daily operations",
      },
      {
        icon: "MailMinus",
        text: "Clear booking details reduce follow-up emails by default",
      },
    ],
    trust: "Fully operational from day one.",
  },
  problem: {
    headline: "Booking software solves the easy part. The chaos starts after.",
    intro: "You take the reservation, then spend your day managing:",
    painPoints: [
      {
        icon: "RefreshCw",
        text: "Manual booking changes and customer requests",
      },
      {
        icon: "Mail",
        text: "Constant emails about pickup times and locations",
      },
      {
        icon: "AlertCircle",
        text: "Unclear return conditions and undocumented damage reports",
      },
      {
        icon: "AlertTriangle",
        text: "Damages that quietly affect upcoming bookings",
      },
      {
        icon: "LayoutGrid",
        text: "Juggling spreadsheets, messaging apps, and paper logs",
      },
    ],
    closing:
      "Every successful booking creates three more operational tasks. And when issues aren\u2019t clearly tracked, one damaged bike can disrupt multiple future reservations.\n\nYour rental business runs \u2014 but you\u2019re always running behind it.",
  },
  positioning: {
    headline: "Operations-first software for real rental days",
    intro:
      "Most rental tools focus on filling your calendar. bikes.rentals focuses on running smooth daily operations after bookings are made.",
    detail:
      "While booking-first platforms leave you to manually coordinate everything that follows, we structure the entire rental process from the first customer interaction to final return.",
    trust: "Built around real rental workflows.",
    benefits: [
      {
        icon: "MessageCircle",
        title: "Fewer customer questions",
        desc: "Automated communication prevents and handles routine inquiries",
      },
      {
        icon: "ListChecks",
        title: "Less manual coordination",
        desc: "Staff know exactly what needs doing when",
      },
      {
        icon: "ClipboardCheck",
        title: "Consistent handovers",
        desc: "Clear protocols for pickups, deliveries, and returns",
      },
      {
        icon: "Eye",
        title: "Operational clarity",
        desc: "Real-time visibility into every rental status",
      },
    ],
  },
  valueBlocks: [
    {
      icon: "ClipboardCheck",
      title: "Clean Booking Flow",
      description:
        "Guided booking that prevents mistakes before they happen. Customers book, modify, extend, or cancel without calling your team. Clear processes reduce errors before they reach daily operations.",
    },
    {
      icon: "MessageCircle",
      title: "Automated Communication",
      description:
        "Automated messages with pickup instructions, booking details, extensions, and return guidelines reduce routine customer questions. Your staff focuses on actual problems, not repetitive information requests.",
    },
    {
      icon: "FileCheck",
      title: "Digital Waiver Collection",
      description:
        "Integrate waiver signing directly into the booking process. Signed documents are automatically attached to each reservation, reducing manual coordination at pickup.",
    },
    {
      icon: "ListChecks",
      title: "Organized Daily Operations",
      description:
        "Clear task lists and defined handover protocols eliminate confusion during busy periods. New staff get up to speed faster with documented processes and consistent workflows.",
    },
    {
      icon: "MapPin",
      title: "Delivery Booking Built-In",
      description:
        "Customers choose delivery options directly in the booking flow \u2014 selectable delivery windows, automated delivery pricing based on distance or traffic-aware travel time, and clear delivery details before checkout. This reduces manual coordination and eliminates back-and-forth pricing discussions.",
    },
  ],
  categoryFrame: {
    headline: "Two approaches to rental software:",
    bookingFirst: {
      title: "Booking-first tools",
      items: [
        { label: "Calendar focus" },
        { label: "Manual coordination after booking" },
        { label: "Growth adds complexity" },
      ],
    },
    operationsFirst: {
      title: "Operations-first platform",
      items: [
        { label: "Defined processes" },
        { label: "Automated workflows" },
        { label: "Growth strengthens systems" },
      ],
    },
    closing:
      "We chose operations-first because successful rental businesses need more than filled calendars. They need calm, controlled daily operations.",
  },
  fit: {
    headline: "Is bikes.rentals right for you?",
    goodFit: {
      title: "Good fit for:",
      items: [
        "Growing fleets across one or multiple locations",
        "Operators managing daily rentals with one or multiple team members",
        "Real operational complexity beyond simple bookings",
        "Owners who want reliable growth and control",
      ],
    },
    notIdeal: {
      title: "Not ideal for:",
      items: [
        "Hobby rental operations",
        "Businesses that only need a booking calendar",
        "Primarily point-of-sale focused bike shops",
      ],
    },
    closing:
      "We work with serious rental operators who understand that smooth operations matter as much as full calendars.",
  },
  growth: {
    headline:
      "Built for rental businesses that outgrow their first booking tool",
    description:
      "Many rental operators start with simple booking software, then hit operational limits around 30\u201350 bikes. Manual processes that worked for small operations create daily stress as volume grows.",
    detail:
      "bikes.rentals handles operational complexity from day one, so adding locations, staff, and inventory strengthens your systems rather than breaking them.",
  },
  setup: {
    headline: "Done-for-you setup. No migration stress.",
    intro:
      "You don\u2019t start with a blank dashboard. We handle the entire onboarding so you start with a fully configured system from day one.",
    points: [
      { icon: "Package", text: "We set up your products and pricing" },
      { icon: "Layers", text: "We structure your inventory" },
      { icon: "MessageSquare", text: "We configure customer communication" },
      { icon: "ArrowRightLeft", text: "We migrate existing bookings if needed" },
      { icon: "Settings", text: "We optimize workflows from day one" },
    ],
    highlights: [
      "No lost bookings",
      "No setup chaos",
      "No technical burden",
    ],
    trust: "Built for daily use, not just demos.",
    selfServiceNote:
      "Advanced 24/7 self-service setups are available separately for operators who want fully automated handovers.",
  },
  pricing: {
    headline: "Simple pricing. No surprises.",
    monthly: { price: "$99", period: "per location / month" },
    annual: {
      price: "$79",
      period: "per location / month",
      note: "billed annually",
      savings: "Save $240 per year.",
    },
    summary:
      "Clear monthly pricing per location. No revenue sharing. No booking fees. No hidden charges.",
    includes: [
      "Full booking engine",
      "Customer-managed booking changes",
      "Automated communication",
      "Delivery booking support",
      "Digital waiver collection",
      "Organized daily operations",
      "Done-for-you setup",
      "Booking migration support",
    ],
    closing:
      "Transparent pricing that scales with your business structure \u2014 not your daily transaction volume.",
    cta: { label: "Start your setup conversation", href: "/start-setup" },
    microcopy: [
      "No commitment required.",
      "Typical setup call: 30 minutes.",
    ],
  },
  addOns: {
    headline: "Optional Add-Ons",
    items: [
      {
        title: "SMS",
        tag: "usage-based",
        description:
          "Send transactional messages via SMS. Pay only for the messages you use.",
      },
      {
        title: "Advanced 24/7 Self-Service",
        tag: "custom setup",
        description:
          "Smart lock integration and fully automated handovers available as an optional extension. Not included in the standard platform pricing.",
      },
    ],
  },
  finalCta: {
    headline: "Ready to run your rentals more calmly?",
    description:
      "See how clear booking and organized operations create calmer rental days.",
    cta: { label: "See how bikes.rentals works", href: "/demo" },
  },
  footer: {
    logo: "bikes.rentals",
    tagline: "Operations-first rental software.",
    sections: [
      {
        title: "Product",
        links: [
          { label: "How it works", href: "/demo" },
          { label: "Pricing", href: "#pricing" },
          { label: "Request demo", href: "/request-demo" },
        ],
      },
      {
        title: "Company",
        links: [
          { label: "About", href: "/about" },
          { label: "Contact", href: "#contact" },
          { label: "Blog", href: "#blog" },
        ],
      },
    ],
    copyright: `\u00A9 ${new Date().getFullYear()} bikes.rentals. All rights reserved.`,
  },
};
